import React, { useEffect, useState } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Input,
  InputGroup,
  InputLeftElement,
  HStack,
  Select,
  Text,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Textarea,
  VStack,
  useDisclosure,
  useToast,
  IconButton,
  Tooltip,
} from '@chakra-ui/react';
import { FiSearch, FiEdit, FiPhone, FiMail } from 'react-icons/fi';
import {
  getAllFleetInquiries,
  updateFleetInquiryStatus,
  FleetInquiry,
  STATUS_COLORS,
} from '../../services/crmService';

// Cast icons as React.ElementType
const FiSearchIcon = FiSearch as React.ElementType;
const FiEditIcon = FiEdit as React.ElementType;
const FiPhoneIcon = FiPhone as React.ElementType;
const FiMailIcon = FiMail as React.ElementType;

interface Props {
  onDataChange: () => void;
}

const FleetInquiriesTab: React.FC<Props> = ({ onDataChange }) => {
  const [inquiries, setInquiries] = useState<FleetInquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedInquiry, setSelectedInquiry] = useState<FleetInquiry | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  useEffect(() => {
    fetchInquiries();
  }, []);

  const fetchInquiries = async () => {
    try {
      setLoading(true);
      const data = await getAllFleetInquiries();
      setInquiries(data);
    } catch (error) {
      console.error('Error fetching fleet inquiries:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch fleet inquiries',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (
    inquiryId: string,
    newStatus: FleetInquiry['status'],
    notes?: string
  ) => {
    try {
      await updateFleetInquiryStatus(inquiryId, newStatus, notes);
      toast({
        title: 'Status updated',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      await fetchInquiries();
      onDataChange();
      onClose();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update status',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  // Filter inquiries
  const filteredInquiries = inquiries.filter((inquiry) => {
    const matchesSearch =
      !searchTerm ||
      inquiry.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inquiry.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inquiry.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = !statusFilter || inquiry.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  return (
    <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
      {/* Filters */}
      <HStack mb={4} spacing={4}>
        <InputGroup flex="1">
          <InputLeftElement pointerEvents="none">
            <FiSearchIcon color="gray.300" />
          </InputLeftElement>
          <Input
            placeholder="Search by company, name, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </InputGroup>
        <Select
          placeholder="All Statuses"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          w="200px"
        >
          <option value="new">New</option>
          <option value="contacted">Contacted</option>
          <option value="quoted">Quoted</option>
          <option value="closed">Closed</option>
        </Select>
      </HStack>

      {/* Table */}
      {filteredInquiries.length > 0 ? (
        <Box overflowX="auto" overflowY="auto" maxH="600px" border="1px solid" borderColor="gray.200" borderRadius="md">
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th>Company</Th>
                <Th>Contact</Th>
                <Th>Email</Th>
                <Th>Phone</Th>
                <Th>Fleet Size</Th>
                <Th>Status</Th>
                <Th>Date</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredInquiries.map((inquiry) => (
                <Tr key={inquiry.id} _hover={{ bg: 'gray.50' }}>
                  <Td fontWeight="bold">{inquiry.company}</Td>
                  <Td>{inquiry.name}</Td>
                  <Td>
                    <HStack>
                      <Text fontSize="sm">{inquiry.email}</Text>
                      <IconButton
                        aria-label="Email"
                        icon={React.createElement(FiMailIcon)}
                        size="xs"
                        variant="ghost"
                        colorScheme="blue"
                        as="a"
                        href={`mailto:${inquiry.email}`}
                      />
                    </HStack>
                  </Td>
                  <Td>
                    <HStack>
                      <Text fontSize="sm">{inquiry.phone}</Text>
                      <IconButton
                        aria-label="Call"
                        icon={React.createElement(FiPhoneIcon)}
                        size="xs"
                        variant="ghost"
                        colorScheme="green"
                        as="a"
                        href={`tel:${inquiry.phone}`}
                      />
                    </HStack>
                  </Td>
                  <Td>
                    <Badge colorScheme="purple">{inquiry.fleetSize}</Badge>
                  </Td>
                  <Td>
                    <Badge colorScheme={STATUS_COLORS.fleet[inquiry.status]}>
                      {inquiry.status}
                    </Badge>
                  </Td>
                  <Td fontSize="sm">{formatDate(inquiry.createdAt)}</Td>
                  <Td>
                    <Tooltip label="Edit">
                      <IconButton
                        aria-label="Edit"
                        icon={React.createElement(FiEditIcon)}
                        size="sm"
                        variant="ghost"
                        colorScheme="blue"
                        onClick={() => {
                          setSelectedInquiry(inquiry);
                          onOpen();
                        }}
                      />
                    </Tooltip>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      ) : (
        <Text color="gray.500" textAlign="center" py={8}>
          {searchTerm || statusFilter
            ? 'No inquiries match your filters'
            : 'No fleet inquiries found'}
        </Text>
      )}

      {/* Edit Modal */}
      {selectedInquiry && (
        <Modal isOpen={isOpen} onClose={onClose} size="xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Fleet Inquiry Details</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack align="stretch" spacing={4}>
                <Box>
                  <Text fontWeight="bold" mb={2}>Company:</Text>
                  <Text>{selectedInquiry.company}</Text>
                </Box>
                <Box>
                  <Text fontWeight="bold" mb={2}>Contact:</Text>
                  <Text>{selectedInquiry.name}</Text>
                  <Text fontSize="sm" color="gray.600">{selectedInquiry.email}</Text>
                  <Text fontSize="sm" color="gray.600">{selectedInquiry.phone}</Text>
                </Box>
                <Box>
                  <Text fontWeight="bold" mb={2}>Fleet Size:</Text>
                  <Badge colorScheme="purple" fontSize="md">{selectedInquiry.fleetSize}</Badge>
                </Box>
                {selectedInquiry.message && (
                  <Box>
                    <Text fontWeight="bold" mb={2}>Message:</Text>
                    <Text fontSize="sm" bg="gray.50" p={3} borderRadius="md">
                      {selectedInquiry.message}
                    </Text>
                  </Box>
                )}
                <Box>
                  <Text fontWeight="bold" mb={2}>Status:</Text>
                  <Select
                    value={selectedInquiry.status}
                    onChange={(e) =>
                      setSelectedInquiry({
                        ...selectedInquiry,
                        status: e.target.value as FleetInquiry['status'],
                      })
                    }
                  >
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="quoted">Quoted</option>
                    <option value="closed">Closed</option>
                  </Select>
                </Box>
                <Box>
                  <Text fontWeight="bold" mb={2}>Notes:</Text>
                  <Textarea
                    value={selectedInquiry.notes || ''}
                    onChange={(e) =>
                      setSelectedInquiry({
                        ...selectedInquiry,
                        notes: e.target.value,
                      })
                    }
                    placeholder="Add notes about this inquiry..."
                    rows={4}
                  />
                </Box>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={() =>
                  handleStatusUpdate(
                    selectedInquiry.id,
                    selectedInquiry.status,
                    selectedInquiry.notes
                  )
                }
              >
                Save Changes
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </Box>
  );
};

export default FleetInquiriesTab;
